import type { Issue } from "contracts/api/issue/responses";
import {
  ISSUE_EFFORTS,
  ISSUE_SEVERITIES,
  ISSUE_STATUSES,
} from "contracts/enums/core";
import { METRIC_CATALOG } from "contracts/report/catalog";
import { createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import type { WebsiteContext } from "../website/store";
import { BlockEditor } from "./BlockEditor";
import { NumberField, TextField } from "./editorFields";
import { toIssueDraft } from "./issueDraft";

const SEVERITIES: Issue["severity"][] = [...ISSUE_SEVERITIES];
const EFFORTS: Issue["effort"][] = [...ISSUE_EFFORTS];
const STATUSES: Issue["status"][] = [...ISSUE_STATUSES];

export function IssueEditor(props: {
  context: WebsiteContext;
  issue: Issue | null;
  defaultMetricKey?: string;
  onClose: () => void;
}) {
  const [draft, setDraft] = createStore(
    toIssueDraft(props.issue, props.defaultMetricKey),
  );
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await props.context.saveIssue(props.issue?.id ?? null, draft);
      props.onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Issue save failed");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!props.issue) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await props.context.archiveIssue(props.issue);
      props.onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Archive failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="scrim scrim-right">
      <aside class="drawer drawer-wide">
        <div class="drawer-head">
          <h3 class="drawer-title">
            {props.issue ? "Edit issue" : "New issue"}
          </h3>
          <button
            class="icon-btn"
            type="button"
            aria-label="Close"
            onClick={props.onClose}
          >
            ✕
          </button>
        </div>

        <div class="drawer-body editor">
          <fieldset class="editor-group">
            <legend>Summary</legend>
            <TextField
              label="Title"
              value={draft.title}
              onInput={(value) => setDraft("title", value)}
            />
            <label class="editor-field">
              <span class="editor-field-label">Metric</span>
              <select
                class="select"
                value={draft.metricKey}
                onChange={(event) =>
                  setDraft(
                    "metricKey",
                    event.currentTarget.value as Issue["metricKey"],
                  )
                }
              >
                <For each={METRIC_CATALOG}>
                  {(metric) => (
                    <option value={metric.key}>{metric.label}</option>
                  )}
                </For>
              </select>
            </label>
            <label class="editor-field">
              <span class="editor-field-label">Severity</span>
              <select
                class="select"
                value={draft.severity}
                onChange={(event) =>
                  setDraft(
                    "severity",
                    event.currentTarget.value as Issue["severity"],
                  )
                }
              >
                <For each={SEVERITIES}>
                  {(severity) => <option value={severity}>{severity}</option>}
                </For>
              </select>
            </label>
            <label class="editor-field">
              <span class="editor-field-label">Effort</span>
              <select
                class="select"
                value={draft.effort}
                onChange={(event) =>
                  setDraft(
                    "effort",
                    event.currentTarget.value as Issue["effort"],
                  )
                }
              >
                <For each={EFFORTS}>
                  {(effort) => <option value={effort}>{effort}</option>}
                </For>
              </select>
            </label>
            <label class="editor-field">
              <span class="editor-field-label">Status</span>
              <select
                class="select"
                value={draft.status}
                onChange={(event) =>
                  setDraft(
                    "status",
                    event.currentTarget.value as Issue["status"],
                  )
                }
              >
                <For each={STATUSES}>
                  {(status) => <option value={status}>{status}</option>}
                </For>
              </select>
            </label>
            <NumberField
              label="Priority weight (legacy)"
              value={draft.impact}
              onInput={(value) => setDraft("impact", value)}
            />
            <TextField
              label="Owner name"
              value={draft.ownerName ?? ""}
              onInput={(value) => setDraft("ownerName", value || null)}
            />
            <TextField
              label="Owner initials"
              value={draft.ownerInitials ?? ""}
              onInput={(value) => setDraft("ownerInitials", value || null)}
            />
          </fieldset>

          <fieldset class="editor-group">
            <legend>Issue detail</legend>
            <BlockEditor
              blocks={draft.blocks}
              onChange={(blocks) => setDraft("blocks", blocks)}
            />
          </fieldset>
        </div>

        <div class="editor-foot">
          <Show when={error()}>
            <span class="gate-error">{error()}</span>
          </Show>
          <Show when={props.issue}>
            <button
              class="btn"
              type="button"
              disabled={saving()}
              onClick={archive}
            >
              Archive
            </button>
          </Show>
          <button
            class="btn"
            type="button"
            disabled={saving()}
            onClick={props.onClose}
          >
            Cancel
          </button>
          <button
            class="btn btn-primary"
            type="button"
            disabled={saving()}
            onClick={save}
          >
            {saving() ? "Saving…" : "Save"}
          </button>
        </div>
      </aside>
    </div>
  );
}
